import React, { useRef, useState } from 'react';
import {
  alpha,
  Box,
  ClickAwayListener,
  Grow,
  List,
  ListItemButton,
  Popper
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ThemeContext } from 'src/store/ContextStore';
import { CONSTANTS, PEDIGREE } from 'src/components/Constants';

const FertilityMenu = ({
  node,
  memberRecords,
  setMemberRecords,
  handleClose,
  setPedigreeState
}) => {
  const { theme } = React.useContext(ThemeContext);

  const hasSpouses = node.spouseIds?.length > 0;
  const multipleSpouses = node.spouseIds?.length > 1;

  // Multi-spouse: the union is picked via a NESTED SUBMENU (same interaction as
  // the sibling twin-type submenu). A Select cannot live inside the context-menu
  // Popper — MUI portals its option list to document.body, so choosing an option
  // registers as a click-away and closes the whole menu before the choice lands.
  const [spouseMenu, setSpouseMenu] = useState({ open: false, spouseId: null });
  const spouseAnchorRef = useRef(null);
  const openSpouseMenu = (event, spouseId) => {
    spouseAnchorRef.current = event.currentTarget;
    setSpouseMenu({ open: true, spouseId });
  };
  const closeSpouseMenu = () => setSpouseMenu({ open: false, spouseId: null });

  const spouseLabel = (spouseId) => {
    const spouse = memberRecords.find((r) => r.pid === spouseId);
    return spouse
      ? `${spouse.firstName || ''} ${spouse.lastName || ''}`.trim() ||
          `UPN ${spouse.upn || spouseId}`
      : `UPN ${spouseId}`;
  };

  const fertilityOf = (spouseId) =>
    spouseId != null
      ? node.spouseRelationships?.[spouseId]?.fertility ?? null
      : node.fertility ?? null;

  // Selected-option highlight: low-opacity teal (the nav-hover treatment) so the
  // label stays readable — the old solid theme.buttonStyle rendered dark-on-dark.
  const selectedSx = (active) =>
    active && theme?.accent
      ? { backgroundColor: alpha(theme.accent, 0.15) }
      : undefined;

  const onChange = (value, spouseId) => {
    const updatedRecords = [...memberRecords];

    const idx = updatedRecords.findIndex((r) => r.pid === node.pid);
    if (idx === -1) return;

    if (hasSpouses && spouseId != null) {
      // Update fertility in spouseRelationships
      const personRels = {
        ...(updatedRecords[idx].spouseRelationships || {})
      };
      personRels[spouseId] = {
        ...(personRels[spouseId] || {}),
        fertility: value
      };
      updatedRecords[idx] = {
        ...updatedRecords[idx],
        fertility: value,
        spouseRelationships: personRels
      };

      // Mirror fertility on the spouse record
      const spouseIdx = updatedRecords.findIndex((r) => r.pid === spouseId);
      if (spouseIdx !== -1) {
        const spouseRels = {
          ...(updatedRecords[spouseIdx].spouseRelationships || {})
        };
        spouseRels[node.pid] = {
          ...(spouseRels[node.pid] || {}),
          fertility: value
        };
        updatedRecords[spouseIdx] = {
          ...updatedRecords[spouseIdx],
          fertility: value,
          spouseRelationships: spouseRels
        };
      }
    } else {
      updatedRecords[idx] = {
        ...updatedRecords[idx],
        fertility: value
      };
    }

    setMemberRecords(updatedRecords);

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    handleClose();
  };

  const optionList = (spouseId) => {
    const current = fertilityOf(spouseId);
    return CONSTANTS.fertilityMap.map((option, idx) => (
      <ListItemButton
        key={idx}
        sx={selectedSx(option.value === current)}
        onClick={() => onChange(option.value, spouseId)}
      >
        {option.label}
      </ListItemButton>
    ));
  };

  if (!multipleSpouses) {
    return (
      <List disablePadding>
        {optionList(hasSpouses ? node.spouseIds[0] : null)}
      </List>
    );
  }

  return (
    <>
      <List disablePadding>
        {node.spouseIds.map((spouseId) => (
          <ListItemButton
            key={spouseId}
            onMouseEnter={(e) => openSpouseMenu(e, spouseId)}
            onClick={(e) => openSpouseMenu(e, spouseId)}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            with {spouseLabel(spouseId)}
            <ChevronRightIcon fontSize="small" sx={{ ml: 1, opacity: 0.5 }} />
          </ListItemButton>
        ))}
      </List>
      <Popper
        open={!!spouseMenu.open}
        anchorEl={spouseAnchorRef.current}
        placement="right"
        transition
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={closeSpouseMenu}>
            <Grow {...TransitionProps}>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 3,
                  ml: 0.5
                }}
              >
                <List>{optionList(spouseMenu.spouseId)}</List>
              </Box>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default FertilityMenu;
